import { Utility } from './utility'

export namespace UtilityItems {
  export function find<Type>(items: Type[], values: Partial<Type>): Type
  export function find<Type>(
    items: Record<string, Type>,
    values: Partial<Type>,
  ): Type
  export function find<Type>(
    items: Type[] | Record<string, Type>,
    values: Partial<Type>,
  ): Type {
    try {
      const item = findOrFail(items as any, values)

      return item
    } catch (error) {
      return null
    }
  }

  export function findOrFail<Type>(
    items: Type[],
    values: Partial<Type>,
  ): Type | never
  export function findOrFail<Type>(
    items: Record<string, Type>,
    values: Partial<Type>,
  ): Type | never
  export function findOrFail<Type>(
    items: Type[] | Record<string, Type>,
    values: Partial<Type>,
  ): Type | never {
    let itemFound: Type | undefined

    if (Array.isArray(items)) {
      // Handle array of items
      itemFound = items.find(item =>
        Object.keys(values).every(key => item[key] === values[key]),
      )
    } else {
      // Handle record of items
      const entries = Object.values(items)

      itemFound = entries.find(item =>
        Object.keys(values).every(key => item[key] === values[key]),
      )
    }

    if (!itemFound) {
      throw new Error(
        `Could not find item with specified criteria ${Utility.stringify(values)}`,
      )
    }

    return itemFound
  }

  export function sortByString<Type>(
    items: Type[],
    key: keyof Type,
    order: 'ASC' | 'DESC' = 'ASC',
  ): Type[] {
    if (order === 'DESC') {
      return items.sort((a: Type, b: Type) =>
        (b[key] as string).localeCompare(a[key] as string),
      )
    } else {
      return items.sort((a: Type, b: Type) =>
        (a[key] as string).localeCompare(b[key] as string),
      )
    }
  }

  export function sortByNumber<Type>(
    items: Type[],
    key: keyof Type,
    order: 'ASC' | 'DESC' = 'ASC',
  ): Type[] {
    if (order === 'DESC') {
      return items.sort(
        (a: Type, b: Type) =>
          ((b[key] as number) ?? 0) - ((a[key] as number) ?? 0),
      )
    } else {
      return items.sort(
        (a: Type, b: Type) =>
          ((a[key] as number) ?? 0) - ((b[key] as number) ?? 0),
      )
    }
  }

  export function sortByDate<Type>(
    items: Type[],
    key: keyof Type,
    order: 'ASC' | 'DESC' = 'ASC',
  ) {
    if (order === 'DESC') {
      return items.sort(
        (a, b) => (b[key] as Date).getTime() - (a[key] as Date).getTime(),
      )
    } else {
      return items.sort(
        (a, b) => (a[key] as Date).getTime() - (b[key] as Date).getTime(),
      )
    }
  }

  export function makeUnique<Type>(items: Type[], key?: keyof Type): Type[] {
    if (key) {
      return makeUniqueByKey(items, key)
    }

    const uniqueSet = new Set(items)
    return Array.from(uniqueSet)
  }

  function makeUniqueByKey<Type>(items: Type[], key: keyof Type): Type[] {
    return items.reduce((itemPrevious, itemCurrent) => {
      if (!itemPrevious.find(item => item[key] === itemCurrent[key])) {
        itemPrevious.push(itemCurrent)
      }
      return itemPrevious
    }, [])
  }

  export function filterByPattern(items: string[], pattern: string): string[] {
    if (Utility.isEmpty(items)) {
      return []
    }

    return items.filter(item => Utility.isPattern(pattern, item))
  }

  export function groupBy<Type>(
    items: Type[],
    key: keyof Type,
  ): Record<string, Type[]> {
    return items.reduce(
      (store, item) => {
        const value = item[key] as string | number

        if (!store[value]) {
          store[value] = []
        }

        store[value].push(item)

        return store
      },
      {} as Record<string, Type[]>,
    )
  }

  export function findIndex<Type>(
    items: Type[],
    values: Partial<Type>,
  ): number {
    const index = items.findIndex(item =>
      Object.keys(values).every(key => item[key] === values[key]),
    )

    return index
  }

  export function remove<Type>(items: Type[], values: Partial<Type>): Type[]
  export function remove<Type>(
    items: Record<string, Type>,
    values: Partial<Type>,
  ): Record<string, Type>
  export function remove<Type>(
    items: Type[] | Record<string, Type>,
    values: Partial<Type>,
  ): Type[] | Record<string, Type> {
    try {
      if (Array.isArray(items)) {
        // Handle array of items
        const itemsUpdated = [...items]
        const index = findIndex(items, values)

        itemsUpdated.splice(index, 1)

        return itemsUpdated
      } else {
        // Handle record of items
        const entry = findOrFail(items, values)
        const key = entry[0]
        const itemsUpdated = { ...items }

        delete itemsUpdated[key]

        return itemsUpdated
      }
    } catch {
      return items
    }
  }
  export function arrayToObject<ItemType>(
    array: ItemType[],
    getKey?: (item: ItemType) => string,
  ): { [key: number]: ItemType } {
    return array.reduce(
      (obj, value, index) => {
        const key = getKey?.(value) ?? index
        obj[key] = value
        return obj
      },
      {} as { [key: number]: ItemType },
    )
  }

  export function chunkArray<Type>(array: Type[], size: number): Type[][] {
    if (size <= 0) {
      return [array]
    }

    return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
      array.slice(i * size, i * size + size),
    )
  }

  export function isEqual<Type>(arrayA: Type[], arrayB: Type[]): boolean {
    if (arrayA.length !== arrayB.length) {
      return false
    }

    if (arrayA.some(a => !arrayB.includes(a))) {
      return false
    }

    if (arrayB.some(b => !arrayA.includes(b))) {
      return false
    }

    return true
  }
}
